import React, { useEffect, useRef } from "react";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils";
import {
  AiNodeContext,
  ChatWorkflowRequestBodyDto,
  UiMessage,
  UiStreamAction,
} from "@intelligems/ig-types";
import { isAbortError } from "@intelligems/ig-utils";
import { ContentExperienceIntakeTranscriptEntry } from "@src/api/ai/classify-content-experience-intake";
import { useGetExperienceCreationChatSessionHistory } from "@src/api/ai/experience-creation-chat/use-get-experience-creation-chat-session-history";
import { aiExperienceCreationChatKeys } from "@src/api/queryKeys";
import { IgAiIcon, IgButton } from "@intelligems/ig-theme";
import Grid from "@mui/material/Grid";
import { useQueryClient } from "@tanstack/react-query";
import { useUiActionWorkflow } from "@src/api/ai/execute-workflows/ui-action-workflow";
import { useThemeTemplates } from "@src/api/shopify-themes/get-theme-templates";
import { useIsUnderOfferLimit } from "@src/features/sharedExperience/hooks/use-is-under-offer-limit";
import { useIsMerchandisingGated } from "@src/features/sharedExperience/hooks/use-merchandising-plan-access";
import { useIsAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAgenticLogicEnabled";
import { useLiveOrgTheme } from "@src/api/shopify-themes/get-themes";
import { useGetShopDomain } from "@src/api/shops/get-domain";
import { ExecutionPanel } from "@src/features/ai/components/WorkflowEditor/execution/ExecutionPanel";
import {
  ExecutionPanelModelOption,
  OnAiChatSend,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import { ExperienceCreationChatHistory } from "@src/features/ai/components/AiChat/ExperienceCreationChatHistory";
import { transformExperienceCreationMessage } from "@src/features/ai/components/AiChat/transformExperienceCreationMessage";
import { useDispatch, useSelector } from "@src/store";
import {
  clearAiChat,
  getAiChat,
  handleAiChatAction,
  restoreChatState,
} from "@src/store/slices/aiChat";
import { handoffCreationToBuilder } from "@src/store/slices/experienceBuilder/ai/thunks/handoffCreationToBuilder";
import { getAiChatProps } from "@src/store/slices/global";

import { WorkflowLoadingOverlay } from "./components/WorkflowLoadingOverlay";
import { useStopChat } from "./hooks/useStopChat";
import { useUiActionsChatState } from "./hooks/useUiActionsChatState";
import { useWorkflowSelectors } from "./hooks/useWorkflowSelectors";
import {
  buildWorkflowVariables,
  createInitialAssistantMessage,
  createUserMessage,
} from "./utils/workflowUtils";
import { RateConversation } from "@src/features/ai/components/WorkflowEditor/execution/components/RateConversation";
import {
  useGetOrg,
  useGetOrgId,
} from "@src/api/organizations/get-organization";

interface ExperienceCreationSingleAgentChatProps {
  onClose?: () => void;
}

/**
 * `AskAtlasBuildExperience` (HomeDashboard / ContentExperiences.route) runs a
 * mini intake conversation to determine category + modification before
 * navigating here. When present, this is the full transcript of that
 * conversation — the last entry (always role "user") becomes the live turn.
 */
interface ExperienceCreationSingleAgentInitialContext extends Partial<AiNodeContext> {
  aiIntake?: {
    transcript: ContentExperienceIntakeTranscriptEntry[];
    /**
     * `ts` of the #ellies_experiences thread the intake classifier already
     * started for this exchange, if any — forwarded into this session's
     * first-turn `variables` so `ExperienceCreationChatSlackService` (via
     * `ExperienceCreationChatTurnView.incomingSlackThreadTs`) continues that
     * same thread instead of starting a second one.
     */
    slackThreadTs?: string;
  };
  /**
   * One-shot hand-off brief from Viv (`handoffBuilderToCreation`) — the
   * mirror of Viv's own `variables.creationBrief`. Only read on this
   * session's first turn (see below); never referenced on later turns, so it
   * naturally drops out after that without needing an explicit clear.
   */
  visualEditBrief?: string;
  /**
   * The merchant's literal last message in Viv, captured alongside
   * `visualEditBrief`. Used as the visible opening bubble for the auto-fired
   * first turn instead of a canned placeholder — `visualEditBrief` still
   * rides along as hidden context.
   */
  visualEditLastMessage?: string;
}

const transcriptEntryToUiMessage = (
  entry: ContentExperienceIntakeTranscriptEntry
): UiMessage => {
  const timestamp = Date.now();
  // File parts go before the text bubble so chips render above the text
  // (ChatGPT-style), matching `createUserMessage`'s ordering for the live turn.
  // `url` is always the durable `s3://` reference — this message ships
  // verbatim to the backend, and a client-only `blob:` URL there makes the
  // AI SDK's file-download step throw. `localPreviewUrl` rides alongside
  // purely for instant local rendering. See `createUserMessage` for the
  // same shape on the live-turn path.
  const fileParts: UiMessage["parts"] = (entry.files ?? []).map((file) => ({
    type: "file",
    url: file.url,
    localPreviewUrl: file.localPreviewUrl,
    mediaType: file.mediaType,
    filename: file.name,
    timestamp,
  }));
  return {
    id: v4(),
    role: entry.role,
    timestamp,
    parts: [
      ...fileParts,
      { id: v4(), type: "text", text: entry.text, timestamp },
    ],
  };
};

// Create initial message once at module load time
const initialAssistantMessage = createInitialAssistantMessage();

const MODEL_OPTIONS: ExecutionPanelModelOption[] = [
  { name: "GPT 5.6", id: "openai:gpt-5.6-sol" },
  { name: "Gemini 2.5 Flash", id: "google:gemini-2.5-flash" },
  {
    name: "Claude 4 Sonnet",
    id: "anthropic:claude-4-sonnet-20250514",
  },
];

/**
 * Experimental single-agent replacement for UiActionsChat: same
 * experience-configuration toolset and shared context/hooks, but talks to
 * WORKFLOW_IDS.EXPERIENCE_CREATION_SINGLE_AGENT (one llm-chat node with the
 * full toolset, no "Set Needs" classifier / condition-gate chain).
 */
export const ExperienceCreationSingleAgentChat = ({
  onClose,
}: ExperienceCreationSingleAgentChatProps) => {
  const { initialContext } = useSelector(
    getAiChatProps<ExperienceCreationSingleAgentInitialContext>
  );
  const dispatch = useDispatch();
  const domain = useGetShopDomain();

  const [model, setModel] = useImmer<string | undefined>(MODEL_OPTIONS[0].id);

  // Use custom hooks for state management
  const {
    panelRef,
    workflowState,
    uiState,
    setShowRating,
    setRestartFromFirstNode,
  } = useUiActionsChatState();

  const {
    variations,
    experience,
    redirects,
    audience,
    experienceKeyMetrics,
    pageTargeting,
    onsiteInjections,
    analyticsProducts,
    offers,
  } = useWorkflowSelectors();

  const org = useGetOrg();
  // Passed into every turn's variables so the agent can warn the merchant
  // before building an offer they cannot enable.
  const isUnderOfferLimit = useIsUnderOfferLimit();
  const isMerchandisingGated = useIsMerchandisingGated();
  // Which rules engine an onsite upsell would run on, so the agent describes
  // the right one (and can offer the store's real rules) before handing off.
  const isAgenticLogicEnabled = useIsAgenticLogicEnabled();
  // API hooks
  const liveThemeId = useLiveOrgTheme();
  const themeTemplates = useThemeTemplates(liveThemeId);
  const aiChat = useSelector(getAiChat);

  const { chatWorkflow } = useUiActionWorkflow(setRestartFromFirstNode);
  const stopChat = useStopChat(chatWorkflow, aiChat.sessionId);
  const queryClient = useQueryClient();

  // Session id whose transcript we're loading from chat history. Set by
  // `onSessionIdChange`; cleared once `restoreChatState` has rehydrated redux.
  const [loadedSessionId, setLoadedSessionId] = useImmer<string | null>(null);
  const sessionHistory = useGetExperienceCreationChatSessionHistory({
    sessionId: loadedSessionId,
    enabled: !!loadedSessionId,
  });

  // Rehydrate redux with a past conversation once its transcript arrives.
  // `restoreChatState` Object.assigns the full AiChatState (including
  // `sessionId`), so subsequent sends continue that session instead of
  // starting a new one.
  useEffect(() => {
    if (loadedSessionId && sessionHistory.isSuccess && sessionHistory.data) {
      dispatch(restoreChatState(sessionHistory.data));
      setLoadedSessionId(null);
    }
  }, [
    loadedSessionId,
    sessionHistory.isSuccess,
    sessionHistory.data,
    dispatch,
    setLoadedSessionId,
  ]);

  const onSessionIdChange = (id: string) => {
    dispatch(clearAiChat());
    if (id === "new") {
      // Fresh chat: cleared redux means the next send takes the
      // `!aiChat.sessionId` branch and initializes a new session.
      setLoadedSessionId(null);
    } else {
      setLoadedSessionId(id);
    }
  };

  const isAnyPending = chatWorkflow.isPending;
  const hasAutoStartedFromIntake = useRef(false);

  useEffect(() => {
    const metadataModel =
      typeof initialContext?.metadata?.model === "string"
        ? initialContext.metadata.model
        : undefined;

    if (metadataModel) {
      setModel(metadataModel);
    }
  }, [initialContext?.metadata?.model, setModel]);

  /**
   * TEMPORARY WORKAROUND (debugging aid, not a real fix): when
   * `chatWorkflow.mutate` rejects for a reason other than an intentional
   * abort (e.g. the connection itself dying — ERR_HTTP2_PROTOCOL_ERROR — before
   * any terminal `UiStreamAction` reaches the browser), nothing ever moves
   * `aiChat.status` off `"active"`/`"processing"`, so the prompt input stays
   * disabled forever. Dispatching `workflowPaused` is a side-effect-free way
   * to force it out of that stuck state (it's a plain `draft.status = "paused"`
   * assignment with no turn/node lookups) so the chat is usable again while we
   * debug why sends are failing. Remove once the root-cause fix lands in
   * `useChatWorkflowShared` / `AiChatStateProcessor.handleSetError`.
   */
  const recoverFromSendFailure = (err: unknown, sessionId: string) => {
    if (isAbortError(err)) return;
    // eslint-disable-next-line no-console
    console.error("[ExperienceCreationSingleAgentChat] send failed", err);
    dispatch(
      handleAiChatAction({
        type: "workflowPaused",
        sessionId,
        nodeId: "client-send-failure",
        nextNodeId: "client-send-failure",
        timestamp: Date.now(),
      })
    );
  };

  const onSend: OnAiChatSend = {
    chat: (payload: ChatWorkflowRequestBodyDto) => {
      const variables = buildWorkflowVariables({
        experience,
        domain: domain.data,
        orgId: org.data.org.id,
        shopId: org.data.org.shopId,
        variations,
        redirects,
        onsiteInjections,
        audience,
        experienceKeyMetrics,
        themeTemplates: themeTemplates?.data?.templates || [],
        pageTargeting,
        analyticsProducts,
        started: !!aiChat.sessionId,
        isUnderOfferLimit,
        isMerchandisingGated,
        isAgenticLogicEnabled,
        offers,
        experienceStartedAt: experience.startedAtTs,
      });

      setShowRating(true);

      // Stable id shared between the optimistic user bubble and the server
      // echo so the reducer reconciles by id instead of appending a duplicate.
      const inputMessageId = v4();
      const { message } = createUserMessage(payload.input);
      if (message) {
        message.id = inputMessageId;
      }
      const nodeId = v4();
      const turnId = v4();

      if (!aiChat.sessionId) {
        const sessionId = v4();

        // AskAtlasBuildExperience already ran the intake conversation
        // (category + modification) before navigating here — the last
        // transcript entry is the same live turn as `payload.input`, so seed
        // history with everything before it and skip the generic canned
        // opener.
        const intakeTranscript = initialContext?.aiIntake?.transcript ?? [];
        const priorMessages: UiMessage[] = intakeTranscript
          .slice(0, -1)
          .map(transcriptEntryToUiMessage);

        const actions: UiStreamAction[] = [
          {
            type: "initialize",
            workflowId: WORKFLOW_IDS.EXPERIENCE_CREATION_SINGLE_AGENT,
            sessionId,
            nodeId,
          },
        ];

        for (const priorMessage of priorMessages) {
          actions.push({
            type: "addMessage",
            turnId: v4(),
            message: priorMessage,
            nodeId: v4(),
            nodeName: priorMessage.role === "user" ? "User Input" : "Atlas",
          });
        }

        if (message) {
          actions.push({
            type: "addMessage",
            turnId,
            message,
            nodeId,
            nodeName: "User Input",
          });
        }
        dispatch(handleAiChatAction(actions));

        const vars = {
          ...variables,
          ...(initialContext?.visualEditBrief && {
            visualEditBrief: initialContext.visualEditBrief,
          }),
          ...(initialContext?.aiIntake?.slackThreadTs && {
            slackThreadTs: initialContext.aiIntake.slackThreadTs,
          }),
        };

        void chatWorkflow
          .mutate({
            initialContext: {
              ...initialContext,
              // `visualEditBrief` is only ever present in `initialContext` on
              // this very first send, and only ever read here — no explicit
              // clear needed, it just never comes up again.
              variables: vars,
              sessionId,
              messages:
                priorMessages.length > 0
                  ? [...priorMessages, message]
                  : [initialAssistantMessage, message],
            },
            sessionId,
            workflowId: WORKFLOW_IDS.EXPERIENCE_CREATION_SINGLE_AGENT,
            input: payload.input,
            inputMessageId,
            model: model || undefined,
          })
          .then(() => {
            // Surface the just-started chat in the history dropdown.
            void queryClient.invalidateQueries({
              queryKey: aiExperienceCreationChatKeys.chatHistory(),
            });
          })
          .catch((err) => recoverFromSendFailure(err, sessionId));
      } else {
        if (message) {
          dispatch(
            handleAiChatAction([
              {
                type: "addMessage",
                turnId,
                message,
                nodeId,
                nodeName: "User Input",
              },
            ])
          );
        }

        const continuedSessionId = payload.sessionId || aiChat.sessionId;
        void chatWorkflow
          .mutate({
            sessionId: continuedSessionId,
            input: payload.input,
            inputMessageId,
            variables,
            restartFromFirstNode: workflowState.restartFromFirstNode,
            model,
          })
          .catch((err) => recoverFromSendFailure(err, continuedSessionId));
        setRestartFromFirstNode(true);
      }
    },
    stop: stopChat,
    isPending: chatWorkflow.isPending,
  };

  // Auto-continue the AskAtlasBuildExperience intake conversation: fire the
  // first real turn ourselves instead of waiting for the user to retype
  // what they already told Atlas in the intake box.
  useEffect(() => {
    if (aiChat.sessionId || hasAutoStartedFromIntake.current) return;

    const transcript = initialContext?.aiIntake?.transcript;
    const liveEntry = transcript?.[transcript.length - 1];
    if (!liveEntry || liveEntry.role !== "user") return;

    hasAutoStartedFromIntake.current = true;
    onSend.chat({
      sessionId: v4(),
      input: {
        type: "text",
        content: liveEntry.text,
        files: liveEntry.files,
      },
    });
  }, [initialContext?.aiIntake, aiChat.sessionId, onSend]);

  // Auto-continue a Viv hand-off: the merchant already stated their request
  // over there — fire the opening turn ourselves, using their literal last
  // message as the visible bubble, instead of leaving the chat empty and
  // waiting for them to repeat it. `visualEditBrief` still rides along as
  // hidden context (see the `variables` branch above). Mirrors the intake
  // auto-start above; guarded so the two never race if both happened to be
  // present.
  const hasAutoStartedFromBrief = useRef(false);
  useEffect(() => {
    if (aiChat.sessionId || hasAutoStartedFromBrief.current) return;
    if (initialContext?.aiIntake?.transcript?.length) return;
    if (!initialContext?.visualEditBrief) return;

    hasAutoStartedFromBrief.current = true;
    onSend.chat({
      sessionId: v4(),
      input: {
        type: "text",
        content:
          initialContext.visualEditLastMessage ||
          "Continue with what I just asked in the visual editor.",
      },
    });
  }, [
    initialContext?.visualEditBrief,
    initialContext?.aiIntake,
    aiChat.sessionId,
    onSend,
  ]);

  return (
    <>
      {isAnyPending && (
        <WorkflowLoadingOverlay overlayWidth={uiState.overlayWidth} />
      )}
      <Grid
        container
        direction={"column"}
        wrap={"nowrap"}
        spacing={2}
        sx={{ height: "100%" }}
      >
        <Grid sx={{ flexGrow: 1, minHeight: 0 }}>
          <ExecutionPanel
            title={
              <>
                <IgAiIcon size={32} sx={{ marginRight: 2 }} shine />
                Atlas
              </>
            }
            feature={"create_test"}
            ref={panelRef}
            onClose={onClose}
            onSend={onSend}
            workflowId={WORKFLOW_IDS.EXPERIENCE_CREATION_SINGLE_AGENT}
            showSystemMessages={false}
            showToolCallOutput={false}
            showToolsAsTasks={true}
            showToolsAsSteps={true}
            hiddenTools={[
              "setExperienceDetails",
              "setExperienceMode",
              "setVariations",
              "setUrlRedirects",
              "setTemplateRedirects",
              "setThemeRedirects",
              "switchTestType",
              "getHtml",
              "setCommonAudience",
              "setCustomAudience",
              "setAdvancedAudience",
              "setCurrencyTargeting",
              "setLinkTargeting",
              "setPageTargeting",
              "setProductTargeting",
              "setKeyMetrics",
              "setCustomMetric",
              "setAnalyticsViewType",
              "setExclusionGroup",
              "handoffToVisualEditor",
            ]}
            clearReasoningOnMessage={true}
            showReasoningMessages={false}
            showConditionNodes={false}
            transformMessage={transformExperienceCreationMessage}
            slots={{
              // headerLeft: (
              //   <ExperienceCreationChatHistory
              //     currentSessionId={aiChat.sessionId ?? ""}
              //     onSessionIdChange={onSessionIdChange}
              //   />
              // ),
              promptInputTools: workflowState.showRating ? (
                <RateConversation
                  allowComments={true}
                  metadata={{
                    model: model ?? "",
                    experienceType: experience.type,
                    experienceName: experience.name,
                    experienceId: experience.id!,
                  }}
                />
              ) : null,
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};
